import classNames from 'classnames';
import { getShouldDisplaySticky } from 'components/navigation/is-current-url.js';
import { ColumnLayoutContext } from 'page-context/column-layout/column-layout-context.js';
import { DomRefContext } from 'page-context/domRef/domRefProvider.js';
import { StickyPosition } from 'page-context/scrollStickyStateProvider/scrollStickyMain.js';
import { AppBarStickyState, DisplayState } from 'page-context/scrollStickyStateProvider/stickybar-types.js';
import { CSSProperties, memo, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IColumnLayout } from 'redux-state/layout/types.modules.js';
import { IRepoState } from 'redux-state/rootReducer.js';
import { zIndexNavigation } from 'utilities/css-variables.js';
import { getSearchBarColor, getSearchBarFill } from './search-bar-css.js';
import { MemoSearchBar } from './search-bar.js';

type IMemoisedSearchBar = {
  searchContainerDomRef: (el: HTMLDivElement) => void;
  stickyItemsState: AppBarStickyState | undefined;
  dynamicBarPos: number;
  layoutContext: IColumnLayout;
  hasSecondaryNavBar: boolean;
  children: JSX.Element;
};

/**
 * This hack is  because the navigation is hidden via translate 3d however the parent div is still overlaying content
 * Therefore on pages like /maps-radar/rain/radar?range=300&tab=real-time this div prevents the slider being clicked on certain scenarios
 */
const shouldOverlayContent = (isSticky: boolean, dynamicContent: number) => {
  if (isSticky) {
    if (dynamicContent < -80) {
      return false;
    }
  }
  return true;
};

const SearchBarCore = ({
  dynamicBarPos,
  searchContainerDomRef,
  stickyItemsState,
  layoutContext,
  hasSecondaryNavBar,
  children,
}: IMemoisedSearchBar) => {
  const isOneCol = layoutContext === '1-column';

  const [color, setColor] = useState<'blue' | 'white'>('blue');
  const [fill, setFill] = useState('');

  useEffect(() => {
    const newColor = getSearchBarColor(isOneCol, stickyItemsState);
    setColor(newColor);
    const newFill = getSearchBarFill(hasSecondaryNavBar, stickyItemsState);
    setFill(newFill);
  }, [stickyItemsState]);

  /** When display is forced e.g. favourites selected then override dyanamic offset to force dispplay */
  const calculateDynamic = stickyItemsState?.displayForced === DisplayState.display ? 0 : dynamicBarPos;
  const shouldDisplaySticky = getShouldDisplaySticky(stickyItemsState);
  const shouldDisplayAbovePrimaryContent = shouldOverlayContent(shouldDisplaySticky, calculateDynamic);
  const transform = shouldDisplaySticky ? `translate3d(0, ${calculateDynamic ?? 0}px, 0)` : 'inherit';
  const searchEntryStyle: CSSProperties = shouldDisplaySticky
    ? {
        transform,
        position: 'sticky',
        top: -1,
        zIndex: shouldDisplayAbovePrimaryContent ? zIndexNavigation + 1 : 0,
      }
    : { transform };

  return (
    <div
      data-module={`search-entry dynamic-${dynamicBarPos} sticky-${stickyItemsState?.displayAsSticky} static-${stickyItemsState?.displayAsStatic} ${stickyItemsState?.displayForced}`}
      className="sticky-headers"
      style={searchEntryStyle}
      ref={searchContainerDomRef}
    >
      <div
        data-module="search-bar-section"
        className={classNames('SearchContainer Container-inner', {
          'SearchContainer--blue-solid': fill === 'solid',
          'SearchContainer--blue': color === 'blue',
        })}
      >
        {children}
      </div>
    </div>
  );
};

// https://github.com/facebook/react/issues/15156
const _MemoisedSearchBar = ({
  stickyItemsState,
  searchContainerDomRef,
  dynamicBarPos,
  hasSecondaryNavBar,
  layoutContext,
  children,
}: IMemoisedSearchBar) => (
  <SearchBarCore
    stickyItemsState={stickyItemsState}
    searchContainerDomRef={searchContainerDomRef}
    dynamicBarPos={dynamicBarPos}
    hasSecondaryNavBar={hasSecondaryNavBar}
    layoutContext={layoutContext}
  >
    {children}
  </SearchBarCore>
);

const MemoisedSearchBar = memo(_MemoisedSearchBar);

export const SearchBarMainEntry = () => {
  const navigation = useSelector((state: IRepoState) => state.LAYOUT.screen.layout.navigation);
  const hasSecondaryNavBar = Boolean(navigation?.links?.length);
  return <SearchBarMain hasSecondaryNavBar={hasSecondaryNavBar} />;
};
/**
 * As stickybarContext updates with every scroll update we split the component into two and memoise the search bar from its stickybarContext dependancy
 * Therefore component will only render when appbarsticky or searchocntainerRef changes
 * https://github.com/facebook/react/issues/15156
 */
export const SearchBarMain = ({ hasSecondaryNavBar }: { hasSecondaryNavBar: boolean }) => {
  const { dynamicBarPos, stickyItemsState } = useContext(StickyPosition);
  const { searchBarDomRef } = useContext(DomRefContext);
  const layoutContext = useContext(ColumnLayoutContext);

  return (
    <MemoisedSearchBar
      searchContainerDomRef={(el: HTMLDivElement) => searchBarDomRef?.setDomRef(el)}
      stickyItemsState={stickyItemsState}
      dynamicBarPos={dynamicBarPos}
      layoutContext={layoutContext}
      hasSecondaryNavBar={hasSecondaryNavBar}
    >
      <MemoSearchBar />
    </MemoisedSearchBar>
  );
};
